import React, { useState } from 'react';

// Plain-text reference optimized for pasting into LLMs
const LLM_REFERENCE = `SOQL to SQL Translation Reference
=================================

This is a reference for writing SOQL queries that get translated to DuckDB SQL. Compatibility is 97% (74/76 test queries). Paste this into an LLM for help writing queries.

SYSTEM CONTEXT
--------------
- Queries are written in SOQL syntax
- They get translated to DuckDB SQL and run against synced Salesforce data
- Parent relationships use LEFT JOINs
- Child subqueries use correlated subqueries with JSON aggregation

KEY DIFFERENCES (know these to avoid errors)
--------------------------------------------

1. NOT IN WITH NULL VALUES
   SOQL includes NULL values in NOT IN results.
   SQL excludes NULL values (three-valued logic).

   Example: SELECT Id FROM Account WHERE Industry NOT IN ('Other')
   - Salesforce returns records where Industry is NULL
   - DuckDB excludes records where Industry is NULL

   Workaround: Add OR field = null
   SELECT Id FROM Account WHERE (Industry NOT IN ('Other') OR Industry = null)

2. FIELD ALIASES
   SOQL only allows aliases on aggregate functions, not regular fields.

   WRONG (fails in SOQL): SELECT Name n FROM Account
   RIGHT: SELECT Name FROM Account
   RIGHT: SELECT COUNT(Id) cnt FROM Account

3. NOT LIKE SYNTAX
   SOQL requires: NOT (field LIKE pattern)
   SQL also accepts: field NOT LIKE pattern

   Use this for compatibility: SELECT Id FROM Account WHERE NOT (Name LIKE '%Test%')
   Avoid this: SELECT Id FROM Account WHERE Name NOT LIKE '%Test%'

4. NULL COMPARISONS
   SOQL: field = null, field != null
   SQL: field IS NULL, field IS NOT NULL

   The translator handles this automatically. You can write either syntax.

5. DEFAULT QUERY LIMIT
   Queries default to LIMIT 1000 for safety.
   Use explicit LIMIT for more rows (max 10000).

SUPPORTED DATE LITERALS
-----------------------
TODAY - current date
YESTERDAY - previous day
THIS_WEEK - start of week to now
THIS_MONTH - start of month to now
THIS_QUARTER - start of quarter to now
THIS_YEAR - start of year to now
LAST_N_DAYS:n - n days ago to now (e.g., LAST_N_DAYS:30)
LAST_N_MONTHS:n - n months ago to start of current month
THIS_FISCAL_YEAR - same as THIS_YEAR (calendar year)

Example: SELECT Id FROM Opportunity WHERE CreatedDate > LAST_N_DAYS:30

SUPPORTED DATE FUNCTIONS
------------------------
CALENDAR_MONTH(date) - extracts month (1-12)
CALENDAR_YEAR(date) - extracts year
FISCAL_QUARTER(date) - extracts quarter (1-4)
DAY_IN_MONTH(date) - extracts day of month
HOUR_IN_DAY(date) - extracts hour

Example: SELECT CALENDAR_MONTH(CreatedDate), COUNT(Id) FROM Account GROUP BY CALENDAR_MONTH(CreatedDate)

RELATIONSHIP QUERIES
--------------------
Parent relationships (dot notation):
SELECT Id, Account.Name, Account.Industry FROM Contact
(Translated to LEFT JOIN)

Child subqueries:
SELECT Id, Name, (SELECT Id, FirstName FROM Contacts) FROM Account
(Translated to correlated subquery with JSON aggregation)

Semi-joins:
SELECT Id FROM Account WHERE Id IN (SELECT AccountId FROM Opportunity)

Anti-joins:
SELECT Id FROM Account WHERE Id NOT IN (SELECT AccountId FROM Opportunity)

AGGREGATES
----------
COUNT(field), COUNT(), SUM(field), AVG(field), MIN(field), MAX(field)
GROUP BY, HAVING, ORDER BY all supported.

Example: SELECT Industry, COUNT(Id) cnt FROM Account GROUP BY Industry HAVING COUNT(Id) > 10

NOT SUPPORTED
-------------
- FOR UPDATE / FOR REFERENCE
- WITH SECURITY_ENFORCED
- TYPEOF (polymorphic relationships)
- FORMAT() function
- DISTANCE, GEOLOCATION queries
- USING SCOPE

QUICK TIPS
----------
1. Don't alias non-aggregate fields
2. Use NOT (field LIKE pattern) not field NOT LIKE pattern
3. Add OR field = null when using NOT IN if you need NULLs
4. Use explicit LIMIT if you need more than 1000 rows
5. Parent relationships: Contact.Account.Name (use dot notation)
6. Child subqueries: (SELECT ... FROM ChildRelationshipName)`;

/**
 * QueryTranslatorNotes - Displays SOQL to SQL translation notes
 * Shows a collapsible panel explaining the differences between SOQL and SQL
 */
export default function QueryTranslatorNotes() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyForLLM = async () => {
    try {
      await navigator.clipboard.writeText(LLM_REFERENCE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="query-translator-notes">
      <button
        type="button"
        className="notes-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <span className="info-icon">ℹ</span>
        <span>SOQL Translation Notes</span>
        <span className="chevron">{isExpanded ? '▼' : '▶'}</span>
      </button>

      {isExpanded && (
        <div className="notes-content">
          <p className="compatibility">
            <strong>97% compatibility</strong> with Salesforce SOQL
          </p>

          <h4>Key Differences</h4>

          <div className="note-item">
            <strong>NOT IN with NULL</strong>
            <p>
              SQL excludes NULL values from NOT IN results. If you need NULLs included, add:{' '}
              <code>OR field = null</code>
            </p>
          </div>

          <div className="note-item">
            <strong>Field Aliases</strong>
            <p>
              SOQL only allows aliases on aggregate fields (COUNT, SUM, etc.), not regular fields.
            </p>
          </div>

          <div className="note-item">
            <strong>NOT LIKE Syntax</strong>
            <p>
              Use <code>NOT (field LIKE '%pattern%')</code> instead of{' '}
              <code>field NOT LIKE '%pattern%'</code> for SOQL compatibility.
            </p>
          </div>

          <div className="note-item">
            <strong>Date Literals</strong>
            <p>
              Supported: TODAY, YESTERDAY, THIS_WEEK, THIS_MONTH, THIS_QUARTER, THIS_YEAR,
              LAST_N_DAYS:n, LAST_N_MONTHS:n, THIS_FISCAL_YEAR
            </p>
          </div>

          <div className="note-item">
            <strong>Query Limits</strong>
            <p>
              Default limit is 1000 rows. Use <code>LIMIT</code> clause for more (max 10000).
            </p>
          </div>

          <h4>Fully Supported</h4>
          <ul>
            <li>Parent relationships (Account.Name)</li>
            <li>Child subqueries (SELECT ... FROM Contacts)</li>
            <li>Semi-joins and anti-joins (IN/NOT IN subqueries)</li>
            <li>Aggregates (COUNT, SUM, AVG, MIN, MAX)</li>
            <li>GROUP BY, HAVING, ORDER BY</li>
            <li>Date functions (CALENDAR_MONTH, etc.)</li>
            <li>NULL comparisons (= null, != null)</li>
          </ul>

          <div className="notes-actions">
            <button
              type="button"
              onClick={handleCopyForLLM}
              className="copy-llm-btn"
            >
              {copied ? '✓ Copied!' : 'Copy for LLM'}
            </button>
          </div>
        </div>
      )}

      <style>{`
        .query-translator-notes {
          margin: 1rem 0;
          border: 1px solid var(--border-color, #e0e0e0);
          border-radius: 6px;
          background: var(--bg-secondary, #f9f9f9);
        }

        .notes-toggle {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 0.875rem;
          color: var(--text-secondary, #666);
          text-align: left;
        }

        .notes-toggle:hover {
          background: var(--bg-hover, #f0f0f0);
        }

        .info-icon {
          font-size: 1rem;
          color: var(--info-color, #0066cc);
        }

        .chevron {
          margin-left: auto;
          font-size: 0.75rem;
        }

        .notes-content {
          padding: 0 1rem 1rem;
          font-size: 0.875rem;
          line-height: 1.5;
        }

        .compatibility {
          color: var(--success-color, #2e7d32);
          margin-bottom: 1rem;
        }

        .notes-content h4 {
          margin: 1rem 0 0.5rem;
          font-size: 0.875rem;
          color: var(--text-primary, #333);
        }

        .note-item {
          margin: 0.75rem 0;
          padding-left: 0.75rem;
          border-left: 2px solid var(--border-color, #e0e0e0);
        }

        .note-item strong {
          display: block;
          color: var(--text-primary, #333);
        }

        .note-item p {
          margin: 0.25rem 0 0;
          color: var(--text-secondary, #666);
        }

        .note-item code {
          background: var(--code-bg, #e8e8e8);
          padding: 0.1rem 0.3rem;
          border-radius: 3px;
          font-size: 0.8rem;
        }

        .notes-content ul {
          margin: 0.5rem 0;
          padding-left: 1.5rem;
          color: var(--text-secondary, #666);
        }

        .notes-content li {
          margin: 0.25rem 0;
        }

        .notes-actions {
          margin-top: 1rem;
          padding-top: 0.75rem;
          border-top: 1px solid var(--border-color, #e0e0e0);
        }

        .copy-llm-btn {
          padding: 0.5rem 1rem;
          background: var(--primary-color, #0066cc);
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 0.8rem;
          cursor: pointer;
          transition: background 0.2s;
        }

        .copy-llm-btn:hover {
          background: var(--primary-hover, #0052a3);
        }
      `}</style>
    </div>
  );
}
